import { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Mail, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PriceTicker() {
  const [prices, setPrices] = useState(null);
  const fetchPrices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/prices`);
      setPrices(data);
    } catch (err) {
      // Silent: ticker is non-critical UI; backend serves cached fallback when offline
      console.warn('Price ticker fetch failed:', err?.message);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  if (!prices) return null;

  const items = [
    { label: 'Gold', price: prices.gold_nzd, change: prices.changes?.gold?.change_pct },
    { label: 'Silver', price: prices.silver_nzd, change: prices.changes?.silver?.change_pct },
    { label: 'Platinum', price: prices.platinum_nzd, change: prices.changes?.platinum?.change_pct },
    { label: 'Palladium', price: prices.palladium_nzd, change: prices.changes?.palladium?.change_pct },
    { label: 'Copper', price: prices.copper_nzd, change: prices.changes?.copper?.change_pct },
  ].filter(it => it.price && it.price > 0);

  const tickerItems = [...items, ...items, ...items, ...items];

  return (
    <div className="price-ticker" data-testid="price-ticker">
      <div className="ticker-content">
        {tickerItems.map((item, i) => (
          <span key={`${item.label}-${i}`} className="inline-flex items-center gap-2 text-sm">
            <span className="text-gray-400 font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>{item.label}</span>
            <span className="text-[#D4AF37] font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
              NZ${item.price?.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}
            </span>
            {item.change ? (
              <span className={`text-xs font-medium ${item.change > 0 ? 'text-emerald-400' : item.change < 0 ? 'text-red-400' : 'text-gray-500'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                {item.change > 0 ? '+' : ''}{item.change?.toFixed(2)}%
              </span>
            ) : (
              <span className="text-gray-600">/oz</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Buy' },
    { to: '/sell', label: 'Sell' },
    { to: '/live-prices', label: 'Live Prices' },
    { to: '/education', label: 'Learn' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-lg' : ''}`} style={{ background: '#0A0A0A' }} data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-1.5" data-testid="nav-logo">
            <img src="/rose.png" alt="SBE" className="h-16 sm:h-[5rem] md:h-[6.8rem] w-16 sm:w-[5rem] md:w-[6.8rem] object-contain" />
            <span className="text-[#D4AF37] text-[11px] sm:text-sm md:text-base font-semibold tracking-wide leading-tight" style={{ fontFamily: "Cinzel, serif" }}>
              Southern Bullion<br />Exchange
            </span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(l => (
              <Link key={l.to} to={l.to} className={`nav-link text-sm font-semibold tracking-wide uppercase ${location.pathname === l.to ? 'text-[#D4AF37]' : 'text-gray-300 hover:text-white'}`} style={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }} data-testid={`nav-${l.label.toLowerCase().replace(' ', '-')}`}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:0211531763" className="text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-phone">
              <Phone size={18} />
            </a>
            <Link to="/products" className="btn-gold text-sm px-6 py-2.5" data-testid="nav-cta">
              Get a Quote
            </Link>
          </div>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-white" data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[#0A0A0A] border-t border-gray-800 px-6 pb-6">
          {links.map(l => (
            <Link key={l.to} to={l.to} className="block py-3 text-gray-300 hover:text-[#D4AF37] border-b border-gray-800/50 uppercase text-sm tracking-wide" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`mobile-nav-${l.label.toLowerCase().replace(' ', '-')}`}>
              {l.label}
            </Link>
          ))}
          <a href="tel:0211531763" className="block py-3 text-[#D4AF37] font-semibold mt-2">Call: 021 153 1763</a>
          <a href="tel:033596060" className="block py-3 text-[#D4AF37] font-semibold">Call: (03) 359 6060</a>
        </div>
      )}
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-gray-400" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="mb-4 flex flex-col items-start">
              <img src="/rose.png" alt="SBE" className="h-24 w-24 object-contain mb-0.5" />
              <span className="text-[#D4AF37] text-sm font-semibold tracking-wide" style={{ fontFamily: "Cinzel, serif" }}>
                Southern Bullion Exchange
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-4">Silver & Gold - Value You Can Hold. Christchurch's trusted precious metals dealer since 2022.</p>
            <p className="text-xs text-gray-600">Southern Bullion Exchange Limited</p>
          </div>
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontFamily: "'Poppins', sans-serif" }}>Quick Links</h4>
            <div className="flex flex-col gap-2">
              {[{to:'/products',l:'Buy Bullion'},{to:'/sell',l:'Sell to Us'},{to:'/live-prices',l:'Live Prices'},{to:'/education',l:'Education Hub'},{to:'/about',l:'About Us'},{to:'/contact',l:'Contact'}].map(i => (
                <Link key={i.to} to={i.to} className="text-sm hover:text-[#D4AF37] transition-colors">{i.l}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontFamily: "'Poppins', sans-serif" }}>Contact</h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2 text-sm">
                <MapPin size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span>Shop 9 Bishopdale Court<br/>Bishopdale 8053<br/>Christchurch</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone size={16} className="text-[#D4AF37] flex-shrink-0" />
                <div className="flex flex-col">
                  <a href="tel:0211531763" className="hover:text-[#D4AF37] transition-colors">021 153 1763</a>
                  <a href="tel:033596060" className="hover:text-[#D4AF37] transition-colors">(03) 359 6060</a>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <Mail size={16} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <a href="mailto:southernbullionnz@gmail.com" className="hover:text-[#D4AF37] transition-colors">southernbullionnz@gmail.com</a>
                  <a href="mailto:joe@southernbullionex.co.nz" className="hover:text-[#D4AF37] transition-colors">joe@southernbullionex.co.nz</a>
                  <a href="mailto:ethan@southernbullionex.co.nz" className="hover:text-[#D4AF37] transition-colors">ethan@southernbullionex.co.nz</a>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-white text-sm font-semibold mb-4 uppercase tracking-wider" style={{ fontFamily: "'Poppins', sans-serif" }}>Legal</h4>
            <div className="flex flex-col gap-2">
              <Link to="/terms" className="text-sm hover:text-[#D4AF37] transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="text-sm hover:text-[#D4AF37] transition-colors">Privacy Policy</Link>
              <Link to="/aml-cft" className="text-sm hover:text-[#D4AF37] transition-colors">AML/CFT Policy</Link>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-600 mb-2">Partner</p>
              <a href="https://dillongage.com/" target="_blank" rel="noopener noreferrer" data-testid="dillon-gage-link">
                <img src="https://customer-assets.emergentagent.com/job_sbe-preview/artifacts/8wlw7y1d_DG-logofull-white-220x93-1.png" alt="Dillon Gage Metals Division" className="h-10 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" loading="lazy" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-600">&copy; {new Date().getFullYear()} Southern Bullion Exchange Limited. All rights reserved.</p>
          <p className="text-xs text-gray-600">Information on this site is general in nature and is not personalised financial advice.</p>
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-50 bg-[#D4AF37] text-[#0A0A0A] p-3 rounded-sm shadow-lg hover:bg-[#A28026] transition-all" data-testid="scroll-to-top">
      <ChevronUp size={20} />
    </button>
  );
}

export default function Layout() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <PriceTicker />
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
